import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAuth } from './AuthContext'

const WebSocketContext = createContext()

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket debe ser usado dentro de un WebSocketProvider')
  }
  return context
}

export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false)
  const [messages, setMessages] = useState([])
  const [connectionId, setConnectionId] = useState(null)
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const { user, token } = useAuth()

  const connect = () => {
    if (!token || !user) {
      console.log('No hay token o usuario, no se puede conectar WebSocket')
      return
    }

    // Generar un ID único para esta conexión
    const connId = uuidv4()
    setConnectionId(connId)

    // URL del WebSocket - usar la misma URL base que el API
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
    const wsUrl = apiUrl.replace('http', 'ws') + '/ws'
    
    try {
      const wsUrlWithParams = `${wsUrl}?token=${encodeURIComponent(token)}&channelId=main&connectionId=${connId}`
      console.log('Conectando a WebSocket:', wsUrlWithParams.replace(token, '***'))
      
      wsRef.current = new WebSocket(wsUrlWithParams)

      wsRef.current.onopen = () => {
        console.log('WebSocket conectado exitosamente')
        setIsConnected(true)
        
        // Limpiar timeout de reconexión si existe
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }
      }

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('Mensaje recibido:', data)
          setMessages(prev => [...prev, { ...data, received: true, timestamp: new Date().toISOString() }])
        } catch (error) {
          console.error('Error al parsear mensaje:', error)
        }
      }

      wsRef.current.onclose = () => {
        console.log('WebSocket desconectado')
        setIsConnected(false)
        
        // Reconectar después de 3 segundos si hay token
        if (token) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Intentando reconectar...')
            connect()
          }, 3000)
        }
      }

      wsRef.current.onerror = (error) => {
        console.error('Error en WebSocket:', error)
        setIsConnected(false)
      }
    } catch (error) {
      console.error('Error al crear WebSocket:', error)
    }
  }

  const disconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    
    setIsConnected(false)
    setConnectionId(null)
  }

  const sendMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const messageWithId = {
        ...message,
        connectionId,
        messageId: uuidv4(),
        timestamp: new Date().toISOString()
      }
      
      console.log('Enviando mensaje:', messageWithId)
      wsRef.current.send(JSON.stringify(messageWithId))
      setMessages(prev => [...prev, { ...messageWithId, sent: true }])
    } else {
      console.warn('WebSocket no está conectado')
    }
  }

  // Funciones para manejar canales
  const joinChannel = (channelId) => {
    sendMessage({
      type: 'join-channel',
      channelId
    })
  }

  const leaveChannel = (channelId) => {
    sendMessage({
      type: 'leave-channel',
      channelId
    })
  }

  const sendToChannel = (channelId, message) => {
    sendMessage({
      type: 'channel-message',
      channelId,
      message
    })
  }

  // Función ping para verificar conexión
  const ping = () => {
    sendMessage({
      type: 'ping'
    })
  }

  useEffect(() => {
    if (user && token) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [user, token])

  const value = {
    isConnected,
    messages,
    connectionId,
    sendMessage,
    connect,
    disconnect,
    joinChannel,
    leaveChannel,
    sendToChannel,
    ping
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}